import Layout from '@/components/Layout';
import { SEO } from '@/components/SEO';
import { Card, CardContent } from "@/components/ui/card";
import { useToast } from "@/components/ui/use-toast";
import { useAuth } from "@/context/AuthContext";
import { useEffect, useState, useRef } from 'react';
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import { useParams, useNavigate } from 'react-router-dom';
import { Separator } from "@/components/ui/separator";
import { useAiPageContext } from '@/context/AiPageContextProvider';
import { contextHelpers } from '@/components/AiCommandPalette';
import { MdOutlineMoreVert } from 'react-icons/md';
import { TbFileText } from 'react-icons/tb';
// Task Details Components
import TaskActivityTimeline from "@/components/TaskDetails/TaskActivityTimeline";
import TaskAttachments from "@/components/TaskDetails/TaskAttachments";
import TaskBreadcrumbRow from "@/components/TaskDetails/TaskBreadcrumbRow";
import TaskCommentEditor from "@/components/TaskDetails/TaskCommentEditor";
import TaskDescription from "@/components/TaskDetails/TaskDescription";
import TaskSidebar from "@/components/TaskDetails/TaskSidebar";
import TaskSummary from "@/components/TaskDetails/TaskSummary";
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { TbAlertTriangle } from "react-icons/tb";

const DeleteConfirmationModal = ({ isOpen, onClose, onDelete, deleteLoading }) => {
    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <TbAlertTriangle className="h-5 w-5 text-red-500" />
                        <span>Permanently Delete Task</span>
                    </DialogTitle>
                </DialogHeader>
                <DialogDescription>
                    <p className="text-primary">Are you sure you want to delete this task?</p>
                    <p className="mt-3 text-xs text-muted-foreground italic">This action cannot be undone and should be used with caution.</p>
                </DialogDescription>
            <DialogFooter>
                <Button variant="outline" onClick={onClose}>Cancel</Button>
                <Button variant="destructive" onClick={onDelete} disabled={deleteLoading}>
                    {deleteLoading ? (
                        "Deleting..."
                    ) : (
                        "Delete"
                    )}
                </Button>
            </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

const TaskDetails = () => {
    const { organizationSlug, taskKey } = useParams();
    const { toast } = useToast();
    const [task, setTask] = useState(null);
    const [loadingStates, setLoadingStates] = useState({
        core: true,
        description: true,
        activities: true,
        sidebar: true,
    });
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [isDeleteConfirmationModalOpen, setIsDeleteConfirmationModalOpen] = useState(false);
    const { activeOrganization, axiosInstance } = useAuth();
    const descriptionEditorRef = useRef(null);
    const commentEditorRef = useRef(null);
    const { setCurrentAiPageContext } = useAiPageContext();
    const navigate = useNavigate();
    useEffect(() => {
        const fetchTask = async () => {
            try {
                const response = await axiosInstance.get(`/tasks/${organizationSlug}/${taskKey}`);
                const taskData = response.data;
                setTask(taskData);

                // Create custom loading texts specific to this task
                const customLoadingTexts = [
                    {
                        text: `Looking up details for ${taskData.key}`,
                        showOnce: true,
                        minTime: 2000,
                        maxTime: 3000
                    },
                    {
                        text: `Checking status of "${taskData.summary}"`,
                        showOnce: true,
                        minTime: 2500,
                        maxTime: 3500
                    },
                    {
                        text: "Thinking",
                        showOnce: false,
                        minTime: 1500,
                        maxTime: 2500
                    },
                    {
                        text: `Processing information about ${taskData.key}`,
                        showOnce: false,
                        minTime: 2800,
                        maxTime: 4000
                    }
                ];

                // Set page context for AI Command Palette
                setCurrentAiPageContext(
                    contextHelpers.createTaskContext(taskData, customLoadingTexts)
                );

                // Manually set the description editor content
                // This handles edge case where the url changes but stays on the task details page (i.e. ai command palette or task links)
                if (descriptionEditorRef?.current) {
                    descriptionEditorRef.current.commands.setContent(taskData.description);
                }
            } catch (error) {
                console.error('Error fetching task:', error);
                toast({
                    description: "Failed to fetch task details",
                    variant: "destructive"
                });
            } finally {
                setLoadingStates({
                    core: false,
                    description: false,
                    activities: false,
                    sidebar: false,
                });
            }
        };

        fetchTask();
    }, [taskKey, organizationSlug]);

    const handleTaskChange = (updatedTask) => {
        setTask(updatedTask);

        // Only update context when there are significant changes to the task
        // that would affect the context (e.g., summary change)
        if (updatedTask && (!task || updatedTask.summary !== task.summary)) {
            // Create task context with custom loading texts that are specific to this task
            const customLoadingTexts = [
                {
                    text: `Looking up details for ${updatedTask.key}`,
                    showOnce: true,
                    minTime: 2000,
                    maxTime: 3000
                },
                {
                    text: `Checking status of "${updatedTask.summary}"`,
                    showOnce: true,
                    minTime: 2500,
                    maxTime: 3500
                },
                {
                    text: "Thinking",
                    showOnce: false,
                    minTime: 1500,
                    maxTime: 2500
                },
                {
                    text: `Processing information about ${updatedTask.key}`,
                    showOnce: false,
                    minTime: 2800,
                    maxTime: 4000
                }
            ];

            setCurrentAiPageContext(
                contextHelpers.createTaskContext(updatedTask, customLoadingTexts)
            );
        }
    };

    const handleDeleteTask = async () => {
        try {
            setDeleteLoading(true);
            const projectKey = task.projectKey;
            const response = await axiosInstance.delete(`/tasks/${organizationSlug}/${taskKey}`);
            if (response.status === 200) {
                toast({
                    description: "Task deleted successfully",
                });
                const savedBoardView = JSON.parse(localStorage.getItem('board'))?.view;
                if (savedBoardView) {
                    navigate(`/${organizationSlug}/plan/${projectKey}/${savedBoardView}`);
                } else {
                    navigate(`/${organizationSlug}/plan/${projectKey}/board`);
                }
            }
        } catch (error) {
            console.error('Error deleting task:', error);
            toast({
                description: "Failed to delete task",
                variant: "destructive"
            });
        } finally {
            setDeleteLoading(false);
        }
    };

    const handleOpenDeleteConfirmationModal = () => {
        setIsDeleteConfirmationModalOpen(true);
    };

    if (!task && !loadingStates.core) {
        return <Layout>Task not found</Layout>;
    }

    return (
        <>
            <SEO
                title={task ? `${task.summary} (${taskKey})` : 'Task Details'}
                description={task ? `View details and updates for task ${taskKey}` : 'Loading task details...'}
            />
            <Layout>
                <div className="flex justify-between items-center">
                    <TaskBreadcrumbRow isLoading={loadingStates.core} organizationSlug={organizationSlug} task={task} />
                    {activeOrganization.role.toLowerCase() === 'admin' && (
                        <DropdownMenu >
                            <DropdownMenuTrigger asChild>
                                <Button size="icon" variant="secondary">
                                    <MdOutlineMoreVert className="h-5 w-5" />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" >
                                <DropdownMenuItem
                                    className="text-red-600 focus:text-red-600"
                                    onClick={handleOpenDeleteConfirmationModal}
                                >
                                    <TbAlertTriangle className="mr-2 h-4 w-4" />
                                    Delete Task
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    )}
                </div>

                <div className="grid grid-cols-4 gap-6 mt-5">
                    <div className="col-span-4 md:col-span-3 space-y-6 pb-6">
                        <Card>
                            <CardContent className="pt-8 pb-10">
                                <div className="max-w-3xl mx-auto w-full">
                                    <TaskSummary
                                        isLoading={loadingStates.core}
                                        task={task}
                                        onTaskChange={handleTaskChange}
                                    />

                                    <TaskDescription
                                        isLoading={loadingStates.description}
                                        task={task}
                                        onTaskChange={handleTaskChange}
                                        editorRef={descriptionEditorRef}
                                    />

                                    <div className="mt-3">
                                        <TaskAttachments
                                            task={task}
                                            onTaskChange={handleTaskChange}
                                            descriptionEditorRef={descriptionEditorRef}
                                        />
                                    </div>
                                    <Separator className="mt-6" />
                                    <div className="md:hidden">
                                        <TaskSidebar
                                            isLoading={loadingStates.sidebar}
                                            task={task}
                                            onTaskChange={handleTaskChange}
                                            organizationSlug={organizationSlug}
                                        />
                                    </div>
                                    <div className="mt-6">
                                        <TaskActivityTimeline
                                            isLoading={loadingStates.activities}
                                            activities={task?.activities}
                                            uncombinedActivities={task?.uncombinedActivities}
                                            availableStatuses={task?.availableStatuses}
                                        />
                                        <div className="mt-5">
                                            <TaskCommentEditor
                                                isLoading={loadingStates.core}
                                                task={task}
                                                onTaskChange={handleTaskChange}
                                                editorRef={commentEditorRef}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                    <div className="hidden md:block">
                        <TaskSidebar
                            isLoading={loadingStates.sidebar}
                            task={task}
                            onTaskChange={handleTaskChange}
                            organizationSlug={organizationSlug}
                        />
                    </div>
                </div>
            </Layout>
            <DeleteConfirmationModal
                isOpen={isDeleteConfirmationModalOpen}
                onClose={() => setIsDeleteConfirmationModalOpen(false)}
                onDelete={handleDeleteTask}
                deleteLoading={deleteLoading}
            />
        </>
    );
};

export default TaskDetails;